import React, { useState, useContext, memo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';
import useOnClickOutside from 'use-onclickoutside';
import { Icon, Popup } from 'semantic-ui-react';
import { createStructuredSelector } from 'reselect';

import { AppState } from 'services/types';
import {
    setHoveredTableCell,
    setSelectedTableCell,
    removeSelectedTableRow
    // @ts-ignore
} from 'services/selectedTableCells/actions';
import {
    makeSelectIsRowHovered,
    makeSelectIsCellHovered,
    makeSelectIsCellSelected
    // @ts-ignore
} from 'services/selectedTableCells/selectors';
import {
    makeSelectVirtualizedTableColumnType,
    makeSelectVirtualizedTableColumnSelectable,
    makeSelectVirtualizedTableColumnClearable,
    makeSelectVirtualizedTableColumnWidthAdjusted,
    makeSelectVirtualizedTableErrorMessage,
    makeSelectVirtualizedTableItemValue
} from 'services/virtualizedTable/selectors';

import { setItemValue } from 'services/virtualizedTable/actions';
import { VirtualizedTableContextProviderValue } from '../../types';
import VirtualizedTableContext from '../VirtualizedTableContext';
import VirtualizedTableInput from '../VirtualizedTableInput';
import VirtualizedTableDropdown from '../VirtualizedTableDropdown';
import VirtualizedTableDatePicker from '../VirtualizedTableDatePicker';
import VirtualizedTableCellValue from '../VirtualizedTableCellValue';
import VirtualizedTableConfirmationCheckbox from '../VirtualizedTableConfirmationCheckbox';
import VirtualizedTableStatusIcon from '../VirtualizedTableStatusIcon';
import { OwnProps, ConnectedProps, Props } from './types';
import {
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_DATE,
    FIELD_TYPE_CONFIRMATION_CHECKBOX,
    FIELD_TYPE_STATUS_ICON
} from '../../constants';
import './styles.scss';

interface State {
    isOpen: boolean;
    isFocused: boolean;
}

export const VirtualizedTableCellComponent = (
    props: Props
): React.ReactElement | null => {
    const ref = React.useRef<HTMLDivElement>(null);

    const {
        itemValue,
        isRowHovered,
        isCellHovered,
        isCellSelected,
        columnType,
        columnSelectable,
        columnClearable,
        columnWidth,
        errorMessage
    } = props;

    const { rowId, columnKey, itemKey } = props;

    const [{ isOpen, isFocused }, setState] = useState<State>({
        isOpen: false,
        isFocused: false
    });

    const {
        editable,
        onItemValueChange
    }: VirtualizedTableContextProviderValue = useContext(
        VirtualizedTableContext
    );

    const handleClickOutside = (): void => {
        if (isCellSelected) {
            props.removeSelectedTableRow(rowId);

            setState((prevState) => {
                return {
                    ...prevState,
                    isFocused: false,
                    isOpen: false
                };
            });
        }
    };

    useOnClickOutside(ref, handleClickOutside);

    const handleMouseEnter = (): void => {
        props.setHoveredTableCell(rowId, columnKey);
    };

    const handleMouseLeave = (): void => {
        props.setHoveredTableCell(rowId);
    };

    const handleClick = (): void => {
        props.setSelectedTableCell(rowId, columnKey);

        setState((prevState) => ({
            ...prevState,
            isFocused: true,
            isOpen: !isOpen && columnType === FIELD_TYPE_DROPDOWN
        }));
    };

    const handleClear = (): void => {
        props?.setItemValue(itemKey as string, columnKey as string, null);

        if (onItemValueChange) {
            onItemValueChange(itemKey as string, columnKey as string, null);
        }
    };

    const handleOpen = (): void => {
        setState((prevState) => ({
            ...prevState,
            isOpen: true
        }));
    };

    const handleClose = (): void => {
        setState((prevState) => ({
            ...prevState,
            isOpen: false
        }));
    };

    const selectableProps =
        columnSelectable && editable
            ? {
                  onClick: handleClick,
                  tabIndex: 0,
                  role: 'button'
              }
            : {};

    if (!rowId || !columnKey || !itemKey) {
        return null;
    }

    let cellComponent = (
        <VirtualizedTableCellValue
            columnType={columnType}
            itemKey={itemKey}
            columnKey={columnKey}
        />
    );

    const isSelectable = columnSelectable && editable;

    const isSelectableEditableDropdown = isSelectable &&
    (isRowHovered || isCellSelected) && columnType === FIELD_TYPE_DROPDOWN

    const showDropdownClearable = columnClearable && itemValue;

    if (
        isRowHovered &&
        isSelectable &&
        columnType === FIELD_TYPE_CONFIRMATION_CHECKBOX
    ) {
        cellComponent = (
            <VirtualizedTableConfirmationCheckbox
                itemKey={itemKey}
                columnKey={columnKey}
            />
        );
    } else if (columnType === FIELD_TYPE_STATUS_ICON) {
        cellComponent = (
            <VirtualizedTableStatusIcon
                itemKey={itemKey}
                columnKey={columnKey}
            />
        );
    } else if (isCellSelected) {
        if (columnType === FIELD_TYPE_STRING) {
            cellComponent = (
                <VirtualizedTableInput
                    itemKey={itemKey}
                    columnKey={columnKey}
                    onItemValueChange={onItemValueChange}
                />
            );
        } else if (columnType === FIELD_TYPE_NUMBER) {
            cellComponent = (
                <VirtualizedTableInput
                    itemKey={itemKey}
                    columnKey={columnKey}
                    onItemValueChange={onItemValueChange}
                    number
                />
            );
        } else if (columnType === FIELD_TYPE_DROPDOWN) {
            cellComponent = (
                <VirtualizedTableDropdown
                    itemKey={itemKey}
                    columnKey={columnKey}
                    isFocused={isFocused}
                    isOpen={isOpen}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onItemValueChange={onItemValueChange}
                />
            );
        } else if (columnType === FIELD_TYPE_DATE) {
            cellComponent = (
                <VirtualizedTableDatePicker
                    itemKey={itemKey}
                    columnKey={columnKey}
                    onItemValueChange={onItemValueChange}
                />
            );
        }
    }

    return (
        <React.Fragment>
            <div
                ref={ref}
                className={classNames('virtualized-table-cell', {
                    hovered: isCellHovered && !isCellSelected,
                    selected: columnSelectable && isCellSelected,
                    open: isOpen,
                    error: errorMessage
                })}
                style={{
                    width: columnWidth
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                {...selectableProps}
            >
                <div
                    className={classNames('virtualized-table-cell-inner', {
                        dropdown: columnType === FIELD_TYPE_DROPDOWN,
                        selectable:
                            isSelectable &&
                            columnType !== FIELD_TYPE_CONFIRMATION_CHECKBOX
                    })}
                >
                    {cellComponent}
                </div>
                {
                    isSelectableEditableDropdown && !showDropdownClearable && (
                        <Icon
                            className="virtualized-table-cell-icon arrow"
                            name="dropdown"
                        />
                    )
                }

                {
                    isSelectableEditableDropdown && showDropdownClearable && (
                        <Icon
                            className="virtualized-table-cell-icon clear"
                            name="dropdown"
                            onClick={() => handleClear()}
                        />
                    )
                }
            </div>
            <Popup
                context={ref}
                content={errorMessage}
                basic
                position="right center"
                open={!!errorMessage && (isCellHovered || isCellSelected)}
            />
        </React.Fragment>
    );
};

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    itemValue: makeSelectVirtualizedTableItemValue(),
    isRowHovered: makeSelectIsRowHovered(),
    isCellHovered: makeSelectIsCellHovered(),
    isCellSelected: makeSelectIsCellSelected(),
    columnType: makeSelectVirtualizedTableColumnType(),
    columnSelectable: makeSelectVirtualizedTableColumnSelectable(),
    columnClearable: makeSelectVirtualizedTableColumnClearable(),
    columnWidth: makeSelectVirtualizedTableColumnWidthAdjusted(),
    errorMessage: makeSelectVirtualizedTableErrorMessage()
});

export default connect(mapStateToProps, {
    setHoveredTableCell,
    setSelectedTableCell,
    removeSelectedTableRow,
    setItemValue
})(memo(VirtualizedTableCellComponent, isEqual));
